/**
 * The worker's own tasks (Phase 5 / P3), injected into the one base slice
 * (D-17) — same convention as `features/shifts/api/shiftsApi.ts`.
 *
 * `getMyTasks` carries no `providesTags`/`invalidatesTags`: `API_TAG_TYPES`
 * (`packages/api-client/src/api/tagTypes.ts`) is frozen and this feature is
 * scoped to `apps/worker-pwa/` only, so a `'Task'` tag cannot be added here.
 * T-14 already asks for "no polling, refetch when the screen mounts" rather
 * than cross-endpoint invalidation, so `TasksPage.tsx` passes
 * `refetchOnMountOrArgChange: true` to its own call and refetches explicitly
 * after `complete`/`unable` — the documented alternative the task brief
 * itself offers, not a workaround.
 */
import { baseApi, zodResponse } from '@saniplus/api-client';
import {
  CreateTaskPhotoResponseSchema,
  MyTasksResponseSchema,
  TaskSchema,
  type CompleteTaskRequest,
  type CreateTaskPhotoRequest,
  type CreateTaskPhotoResponse,
  type MyTasksResponse,
  type Task,
  type UnableTaskRequest,
} from '@saniplus/contracts';

export interface CreateTaskPhotoArgs extends CreateTaskPhotoRequest {
  readonly taskId: string;
}

export interface CompleteTaskArgs extends CompleteTaskRequest {
  readonly taskId: string;
}

export interface UnableTaskArgs extends UnableTaskRequest {
  readonly taskId: string;
}

export const tasksApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /** `GET /me/tasks` — the caller's current-or-next shift's tasks (T-14, T-17). */
    getMyTasks: build.query<MyTasksResponse, void>({
      query: () => ({ url: '/me/tasks' }),
      ...zodResponse(MyTasksResponseSchema),
    }),

    /** `POST /me/tasks/:id/photos` — a presigned PUT; bytes never touch the API (T-11). */
    createTaskPhoto: build.mutation<CreateTaskPhotoResponse, CreateTaskPhotoArgs>({
      query: ({ taskId, ...body }) => ({
        url: `/me/tasks/${taskId}/photos`,
        method: 'POST',
        body,
      }),
      ...zodResponse(CreateTaskPhotoResponseSchema),
    }),

    /** `POST /me/tasks/:id/complete` → `done`. */
    completeTask: build.mutation<Task, CompleteTaskArgs>({
      query: ({ taskId, ...body }) => ({
        url: `/me/tasks/${taskId}/complete`,
        method: 'POST',
        body,
      }),
      ...zodResponse(TaskSchema),
    }),

    /** `POST /me/tasks/:id/unable` → `unable`. Never takes a photo (T-06). */
    unableTask: build.mutation<Task, UnableTaskArgs>({
      query: ({ taskId, ...body }) => ({
        url: `/me/tasks/${taskId}/unable`,
        method: 'POST',
        body,
      }),
      ...zodResponse(TaskSchema),
    }),
  }),
});

export const {
  useGetMyTasksQuery,
  useCreateTaskPhotoMutation,
  useCompleteTaskMutation,
  useUnableTaskMutation,
} = tasksApi;
